import { AuthClient, type TokenStore } from "@its-engineering-and-development/jwt-auth-client";

const ACCESS_TOKEN_KEY = "itseng_access_token";
const LEGACY_REFRESH_TOKEN_KEY = "itseng_refresh_token";

const localStorageTokenStore: TokenStore = {
    getAccessToken() {
        try {
            return localStorage.getItem(ACCESS_TOKEN_KEY);
        } catch {
            return null;
        }
    },
    setAccessToken(token) {
        try {
            localStorage.setItem(ACCESS_TOKEN_KEY, token);
        } catch {
            console.warn("Failed to store access token in localStorage");
        }
    },
    getRefreshToken() {
        return null;
    },
    setRefreshToken() {
        try {
            localStorage.removeItem(LEGACY_REFRESH_TOKEN_KEY);
        } catch {
            console.warn("Failed to clear legacy refresh token from localStorage");
        }
    },
    clear() {
        try {
            localStorage.removeItem(ACCESS_TOKEN_KEY);
            localStorage.removeItem(LEGACY_REFRESH_TOKEN_KEY);
        } catch {
            console.warn("Failed to clear tokens from localStorage");
        }
    },
};

let authClientInstance: AuthClient | null = null;
type RefreshResult = Awaited<ReturnType<AuthClient["refresh"]>>;
let refreshPromise: Promise<RefreshResult> | null = null;
const NON_RETRYABLE_REFRESH_STATUSES = new Set([400, 401, 403]);

const toStatusCode = (value: unknown): number | null => {
    if (typeof value === "number" && Number.isFinite(value)) {
        return value;
    }

    if (typeof value === "string" && value.trim() !== "") {
        const parsed = Number.parseInt(value, 10);
        if (!Number.isNaN(parsed)) {
            return parsed;
        }
    }

    return null;
};

const extractStatusCode = (error: unknown): number | null => {
    if (!error || typeof error !== "object") {
        return null;
    }

    const candidate = error as {
        status?: unknown;
        statusCode?: unknown;
        response?: { status?: unknown };
        cause?: unknown;
    };

    return (
        toStatusCode(candidate.status) ?? toStatusCode(candidate.statusCode) ?? toStatusCode(candidate.response?.status) ?? extractStatusCode(candidate.cause)
    );
};

function getAuthServiceUrl(): string {
    const baseUrl = import.meta.env.VITE_AUTH_SERVICE_URL?.trim();

    if (!baseUrl) {
        throw new Error("VITE_AUTH_SERVICE_URL is not configured");
    }

    return baseUrl.replace(/\/$/, "");
}

function getInstance(): AuthClient {
    if (!authClientInstance) {
        authClientInstance = new AuthClient(
            {
                baseUrl: getAuthServiceUrl(),
                autoRefresh: false,
                onAuthError: (error) => {
                    console.warn("[authClient] Auth error:", error.message);
                },
            },
            localStorageTokenStore,
        );
    }

    return authClientInstance;
}

export const authClient: AuthClient = new Proxy({} as AuthClient, {
    get(_target, prop) {
        const instance = getInstance();
        const value = Reflect.get(instance, prop, instance);

        if (typeof value === "function") {
            return value.bind(instance);
        }

        return value;
    },
});

export async function refreshAccessToken(): Promise<RefreshResult> {
    if (!refreshPromise) {
        const instance = getInstance();
        refreshPromise = instance.refresh().finally(() => {
            refreshPromise = null;
        });
    }

    return refreshPromise;
}

export function isNonRetryableRefreshError(error: unknown): boolean {
    const statusCode = extractStatusCode(error);
    if (statusCode === null) {
        return false;
    }

    return NON_RETRYABLE_REFRESH_STATUSES.has(statusCode);
}
